import {
  Card,
  Col,
  Input,
  Row,
  Select,
  Button,
  Pagination,
  App,
  Popconfirm,
  Modal,
} from "antd";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CHAPTER_LIST,
  CHAPTER_STATUS,
  DELETE_CHAPTER,
  EDIT_CHATPER_ORDER,
} from "../../../apis/apis";
import CulsightPageLoader from "../../../components/CulsightPageLoader";
import {
  DeleteOutlined,
  GlobalOutlined,
  LockOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import debounce from "lodash.debounce";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

const { Option } = Select;

const SortableChapterCard = ({ item, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: "100%",
    height: "100%",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
};

function ChaptersList() {
  const { notification } = App.useApp();
  const Navigate = useNavigate();
  const location = useLocation();
  const course_title =
    location.state?.title || localStorage.getItem("course_title") || "Course";

  const { course_id } = useParams();
  const [chapters, set_chapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search_query_title, set_search_query_title] = useState("");
  const [current_page, set_current_page] = useState(1);
  const [total_chapters, set_total_chapters] = useState(0);
  const [total_pages, set_total_pages] = useState(1);
  const [page_size, set_page_size] = useState(50);

  const showDeleteWarning = (item) => {
    if (item.status) {
      notification.error({
        message: "Error",
        description:
          "Published chapter cannot be deleted. Please unpublish it first.",
      });
      return;
    }

    Modal.confirm({
      title: "Delete Chapter",
      content: (
        <div>
          <p>This action cannot be undone.</p>
          <p>
            Once this chapter is deleted, it cannot be restored or retrieved
            again.
          </p>
        </div>
      ),
      okText: "Delete Permanently",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        delete_chapter(item.id);
      },
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const fetchChapterList = useCallback(
    async (title = "", page = 1, size = page_size) => {
      const FORM_DATA = new FormData();

      FORM_DATA.append("course_id", atob(course_id));
      FORM_DATA.append("page", page);
      FORM_DATA.append("per_page", size);

      if (title) {
        FORM_DATA.append("title", title);
      }

      try {
        const API_CALL = await CHAPTER_LIST(FORM_DATA);

        if (API_CALL?.data?.status) {
          set_chapters(API_CALL.data?.data || []);
          set_current_page(API_CALL.data?.current_page || 1);
          set_total_chapters(API_CALL.data?.total_chapters || 0);
          set_total_pages(API_CALL.data?.total_pages || 1);
        } else {
          set_chapters([]);
        }
      } catch (error) {
        console.error("Network error:", error);
      } finally {
        setLoading(false);
      }
    },
    [course_id, page_size],
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = chapters.findIndex((item) => item.id === active.id);

    const newIndex = chapters.findIndex((item) => item.id === over.id);

    const reordered = arrayMove(chapters, oldIndex, newIndex);

    set_chapters(reordered);

    try {
      setLoading(true);

      const FORM_DATA = new FormData();

      FORM_DATA.append("course_id", atob(course_id));
      FORM_DATA.append("chapter_id", chapters[oldIndex].id);
      FORM_DATA.append("chapter_order", newIndex + 1);

      const response = await EDIT_CHATPER_ORDER(FORM_DATA);

      if (response?.data?.status) {
        notification.success({
          message: "Success",
          description:
            response?.data?.message || "Chapter order updated successfully",
        });

        fetchChapterList(search_query_title, current_page, page_size);
      } else {
        notification.error({
          message: "Error",
          description:
            response?.data?.message || "Unable to update chapter order",
        });
      }
    } catch (error) {
      console.log(error);

      notification.error({
        message: "Error",
        description: "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useRef(
    debounce((value) => {
      setLoading(true);
      set_current_page(1);
      fetchChapterList(value, 1, page_size);
    }, 500),
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleInput = (e) => {
    const value = e.target.value;
    set_search_query_title(value);
    debouncedSearch(value);
  };

  const pagination_on_change = (page) => {
    setLoading(true);
    fetchChapterList(search_query_title, page, page_size);
  };

  const handlePageSizeChange = (current, size) => {
    set_page_size(size);
    set_current_page(1);
    setLoading(true);
    fetchChapterList(search_query_title, 1, size);
  };

  useEffect(() => {
    fetchChapterList("", 1, 50);
  }, [course_id]);

  const selectBefore = (
    <Select defaultValue="Title">
      <Option value="Title">Title</Option>
    </Select>
  );

  const change_status = async (id) => {
    setLoading(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("id", id);

    try {
      const response = await CHAPTER_STATUS(FORM_DATA);

      if (response?.data?.status) {
        notification.success({
          message: "Successful",
          description: response?.data?.message,
        });

        setLoading(false);

        fetchChapterList(search_query_title, current_page, page_size);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const delete_chapter = async (id) => {
    setLoading(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("id", id);

    try {
      const response = await DELETE_CHAPTER(FORM_DATA);

      if (response?.data?.status) {
        notification.success({
          message: "Successful",
          description: response?.data?.message,
        });

        setLoading(false);

        fetchChapterList(search_query_title, current_page, page_size);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <br />

      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={18} lg={20}>
          <Input
            addonBefore={selectBefore}
            placeholder="Search by title"
            onChange={handleInput}
            size="large"
            style={{ width: "100%" }}
            value={search_query_title}
          />
        </Col>

        <Col xs={24} sm={24} md={4} lg={4}>
          <Button
            variant="solid"
            color="green"
            size="large"
            style={{ marginLeft: "10px", float: "right" }}
            onClick={() => Navigate("/add-chapter/" + course_id)}
          >
            Create Chapter
          </Button>
        </Col>
      </Row>

      {loading ? (
        <CulsightPageLoader />
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={chapters.map((item) => item.id)}
              strategy={rectSortingStrategy}
            >
              <Row gutter={[16, 16]} style={{ marginTop: "15px" }}>
                {chapters?.length > 0 ? (
                  chapters.map((item) => (
                    <Col
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      key={item.id}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: "20px",
                      }}
                    >
                      <SortableChapterCard item={item}>
                        {({ attributes, listeners }) => (
                          <Card
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                            }}
                            bodyStyle={{
                              display: "flex",
                              flexDirection: "column",
                              flex: 1,
                            }}
                            extra={
                              <MenuOutlined
                                {...attributes}
                                {...listeners}
                                style={{
                                  cursor: "grab",
                                  fontSize: 18,
                                }}
                              />
                            }
                            actions={[
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => {
                                  localStorage.setItem(
                                    "course_title",
                                    course_title,
                                  );

                                  Navigate("/edit-chapter/" + btoa(item.id), {
                                    state: {
                                      title: course_title,
                                    },
                                  });
                                }}
                              >
                                View
                              </Button>,

                              <Popconfirm
                                title={
                                  item.status
                                    ? "Are you sure you want to Unpublish this chapter ?"
                                    : "Are you sure you want to publish this chapter ?"
                                }
                                onConfirm={() => change_status(item.id)}
                              >
                                <Button
                                  size="small"
                                  style={{
                                    backgroundColor: item?.status
                                      ? "#52c41a"
                                      : "#faad14",
                                    borderColor: item?.status
                                      ? "#52c41a"
                                      : "#faad14",
                                  }}
                                >
                                  {item.status ? (
                                    <GlobalOutlined />
                                  ) : (
                                    <LockOutlined />
                                  )}
                                </Button>
                              </Popconfirm>,

                              <Popconfirm
                                title={
                                  "Are you sure you want to delete this chapter ?"
                                }
                                onConfirm={() => showDeleteWarning(item)}
                              >
                                <Button danger size="small">
                                  <DeleteOutlined />
                                </Button>
                              </Popconfirm>,
                            ]}
                          >
                            <Card.Meta
                              title={
                                <div
                                  style={{
                                    textAlign: "center",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  <div>{item.title}</div>

                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#888",
                                      marginTop: "4px",
                                    }}
                                  >
                                    {item.updated_at}
                                  </div>
                                </div>
                              }
                            />
                          </Card>
                        )}
                      </SortableChapterCard>
                    </Col>
                  ))
                ) : (
                  <Col span={24}>
                    <p
                      style={{
                        textAlign: "center",
                        color: "red",
                      }}
                    >
                      No chapters found
                    </p>
                  </Col>
                )}
              </Row>
            </SortableContext>
          </DndContext>

          {total_pages > 0 && (
            <div style={{ float: "right", marginTop: "20px" }}>
              <Pagination
                current={current_page}
                total={total_chapters}
                pageSize={page_size}
                showSizeChanger
                pageSizeOptions={["50", "100", "150", "200"]}
                onChange={pagination_on_change}
                onShowSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChaptersList;
